import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { UserCircle, PencilSimple, Trash, X, Phone, IdentificationCard } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";

export default function ParentDetail() {
  const { parentId } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const [confirmDel, setConfirmDel] = useState(false);

  const load = () => api.get(`/parents/${parentId}`).then((r) => setP(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, [parentId]); // eslint-disable-line

  const openEdit = () => { setForm({ name: p.name, phone: p.phone || "" }); setEdit(true); };
  const save = async () => {
    if (!form.name?.trim()) { toast.error("Nama wajib diisi"); return; }
    try { await api.put(`/parents/${parentId}`, { name: form.name, phone: form.phone }); toast.success("Data orang tua diperbarui"); setEdit(false); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async () => {
    try { await api.delete(`/parents/${parentId}`); toast.success("Orang tua dihapus"); navigate("/kelola/users"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!p) return <div><PageHeader title="Detail Orang Tua" /><p className="text-center text-stone-400 mt-10">Memuat...</p></div>;

  return (
    <div>
      <PageHeader title="Detail Orang Tua" />
      <div className="px-5 pt-6 pb-8">
        <div className="flex flex-col items-center">
          <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center shadow-md ring-4 ring-white">
            <UserCircle size={48} weight="duotone" className="text-primary" />
          </div>
          <h2 className="font-heading text-xl font-bold text-stone-800 mt-4">{p.name}</h2>
          <p className="text-stone-500">{p.email || p.phone || "—"}</p>
        </div>

        <div className="bg-white rounded-3xl border border-stone-200/60 shadow-sm divide-y divide-stone-100 overflow-hidden mt-5">
          <Row icon={IdentificationCard} k="Nama" v={p.name} />
          <Row icon={Phone} k="Nomor HP" v={p.phone || "—"} />
        </div>

        <div className="mt-4">
          <p className="font-heading font-semibold text-stone-800 mb-2 text-sm">Anak ({p.children?.length || 0})</p>
          <div className="space-y-1.5">
            {(p.children || []).map((c) => (
              <button key={c.id} data-testid={`parent-child-${c.id}`} onClick={() => navigate(`/siswa/${c.id}`)}
                className="w-full bg-white rounded-xl border border-stone-200/60 p-3 text-left shadow-sm active:scale-[0.98] transition-transform">
                <p className="font-semibold text-stone-800 text-sm">{c.name}</p>
                <p className="text-stone-400 text-xs">{c.kelas}{c.nis ? ` • ${c.nis}` : ""}</p>
              </button>
            ))}
            {(!p.children || p.children.length === 0) && <p className="text-stone-400 text-sm">Belum ada anak terhubung.</p>}
          </div>
        </div>

        <button data-testid="parent-edit-btn" onClick={openEdit}
          className="w-full mt-5 bg-primary text-white font-semibold py-3.5 rounded-full flex items-center justify-center gap-2 active:scale-95 transition-transform">
          <PencilSimple size={18} weight="bold" /> Edit Data Orang Tua
        </button>
        <button data-testid="parent-delete-btn" onClick={() => setConfirmDel(true)}
          className="w-full mt-2.5 border-2 border-destructive/40 text-destructive font-semibold py-3 rounded-full flex items-center justify-center gap-2 active:scale-95 transition-transform">
          <Trash size={18} weight="bold" /> Hapus Orang Tua
        </button>
      </div>

      {edit && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-end md:items-center justify-center" onClick={() => setEdit(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg text-stone-800">Edit Data Orang Tua</h3>
              <button onClick={() => setEdit(false)}><X size={22} className="text-stone-400" /></button>
            </div>
            <p className="text-xs text-stone-500 mb-1">Nama</p>
            <input data-testid="parent-edit-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-stone-50 border border-stone-200 rounded-xl py-3 px-3 mb-3 outline-none focus:border-primary" />
            <p className="text-xs text-stone-500 mb-1">Nomor HP</p>
            <input data-testid="parent-edit-phone" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full bg-stone-50 border border-stone-200 rounded-xl py-3 px-3 mb-4 outline-none focus:border-primary" />
            <button data-testid="parent-edit-save" onClick={save}
              className="w-full bg-primary text-white font-semibold py-3.5 rounded-full active:scale-95 transition-transform">Simpan Perubahan</button>
          </div>
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center px-6" onClick={() => setConfirmDel(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-sm rounded-3xl p-5 text-center">
            <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3"><Trash size={26} weight="bold" className="text-destructive" /></div>
            <h3 className="font-heading font-bold text-lg text-stone-800">Hapus Orang Tua?</h3>
            <p className="text-sm text-stone-500 mt-1 mb-4">Apakah yakin menghapus akun <span className="font-semibold">{p.name}</span>? Data tidak dapat dikembalikan.</p>
            <div className="flex gap-2">
              <button onClick={() => setConfirmDel(false)} className="flex-1 bg-stone-100 text-stone-600 font-semibold py-3 rounded-full">Batal</button>
              <button data-testid="parent-delete-confirm" onClick={del} className="flex-1 bg-destructive text-white font-semibold py-3 rounded-full active:scale-95 transition-transform">Hapus</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ icon: Icon, k, v }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <Icon size={18} weight="duotone" className="text-primary shrink-0" />
      <span className="text-stone-400 text-sm w-28 shrink-0">{k}</span>
      <span className="text-stone-700 text-sm font-medium">{v}</span>
    </div>
  );
}
